import React, { ChangeEventHandler, useState } from 'react';

interface SearchBarFormProps {
  submitHandler: (params: any) => any;
  listView: any;
  searchParams: any;
}

export function SearchBarForm({ listView, searchParams, submitHandler }: SearchBarFormProps) {
  const BLANK_FORM_DATA = {
    query: '',
    min_bedrooms: '',
    max_bedrooms: '',
    layout: '',
    min_start_date: '',
    max_start_date: '',
    min_price: '',
    max_price: '',
    lease_type: '',
    pictures: false,
    pets: false,
    laundry: false,
    utilities: false,
    furnished: false,
    parking: false,
    wheelchair_access: false,
    air_conditioning: false,
  };

  const BOOLEAN_KEYS = [
    'pictures',
    'pets',
    'laundry',
    'utilities',
    'furnished',
    'parking',
    'wheelchair_access',
    'air_conditioning',
  ];
  const INITIAL_FORM_DATA: any = { ...BLANK_FORM_DATA, ...searchParams };
  BOOLEAN_KEYS.forEach((k) => {
    INITIAL_FORM_DATA[k] = searchParams[k] === true || searchParams[k] === 'true';
  });

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [moreFiltersShown, setMoreFiltersShown] = useState(
    ['pictures', 'pets', 'laundry', 'utilities', 'furnished', 'parking', 'wheelchair_access', 'air_conditioning'].some(
      (f) => formData[f]
    )
  );

  const handleFormChange: ChangeEventHandler<any> = (evt) => {
    const target = evt.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    setFormData((fData: any) => ({ ...fData, [name]: value }));
  };

  const anyFiltersSet = () => {
    return !Object.keys(formData).every((f) => formData[f] === '' || formData[f] === false || formData[f] === null);
  };

  const clearFilters = () => {
    setFormData(BLANK_FORM_DATA);
    setMoreFiltersShown(false);
    if (submitHandler) {
      submitHandler(BLANK_FORM_DATA);
    }
  };

  const handleSubmit = (evt: any) => {
    // if no submitHandler passed in, fallback to normal form behavior
    if (submitHandler) {
      evt.preventDefault();
      submitHandler(formData);
    }
  };

  return (
    <form className="search-bar" action="/housing" method="get" onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-12 px-3 py-2">
          <label htmlFor="query" className="form-label sr-only">
            Search
          </label>
          <input
            type="text"
            className="form-control"
            id="query"
            name="query"
            placeholder="Search by keyword (e.g. balcony, parking)"
            onChange={handleFormChange}
            value={formData.query}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 col-lg-auto px-3 py-2">
          <label htmlFor="layout" className="form-label">
            Housing type
          </label>
          <select id="layout" className="form-select" onChange={handleFormChange} name="layout" value={formData.layout}>
            <option value="">Any</option>
            <option value="Shared room">Shared room</option>
            <option value="Private room">Private room</option>
            <option value="Entire place">Entire place</option>
          </select>
        </div>

        <div className="col-md-6 col-lg px-3 py-2">
          <label htmlFor="min-price" className="form-label">
            Price
          </label>
          <div className="row g-2">
            <div className="col">
              <div className="input-group">
                <span className="input-group-text ps-2 pe-2">$</span>
                <input
                  style={{ width: '60px' }}
                  className="form-control"
                  type="number"
                  placeholder="Any"
                  id="min-price"
                  aria-label="minimum price"
                  onChange={handleFormChange}
                  name="min_price"
                  value={formData.min_price}
                />
              </div>
            </div>

            <span className="col-auto price-between mx-0 text-center">to</span>

            <div className="col">
              <label htmlFor="max-price" className="sr-only">
                Max price
              </label>
              <div className="input-group">
                <span className="input-group-text ps-2 pe-2">$</span>
                <input
                  style={{ width: '60px' }}
                  className="form-control"
                  type="number"
                  placeholder="Any"
                  id="max-price"
                  aria-label="maximum price"
                  onChange={handleFormChange}
                  name="max_price"
                  value={formData.max_price}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6 col-lg px-3 py-2">
          <label htmlFor="min-bedrooms" className="form-label">
            Bedrooms
          </label>
          <div className="row g-2">
            <div className="col">
              <select
                className="form-select"
                id="min-bedrooms"
                onChange={handleFormChange}
                name="min_bedrooms"
                value={formData.min_bedrooms}
              >
                <option value="">Any</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
              </select>
            </div>

            <span className="col-auto bed-size-between mx-1 text-center">to</span>

            <div className="col">
              <label htmlFor="max-bedrooms" className="sr-only">
                Max bedrooms
              </label>
              <select
                className="form-select"
                id="max-bedrooms"
                onChange={handleFormChange}
                name="max_bedrooms"
                value={formData.max_bedrooms}
              >
                <option value="">Any</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
              </select>
            </div>
          </div>
        </div>

        <div className="col-md-6 col-lg-3 py-2 px-3">
          <div className="pb-2 text-center">
            <a onClick={() => setMoreFiltersShown((moreFiltersShown) => !moreFiltersShown)} href="javascript:void(0);">
              {moreFiltersShown ? 'Hide filters' : 'Show more filters'}
            </a>
            {anyFiltersSet() && (
              <span>
                {` / `}
                <a onClick={clearFilters} href="javascript:void(0);">
                  Clear filters
                </a>
              </span>
            )}
          </div>
          <button className="btn btn-primary w-100" type="submit">
            Search
          </button>
        </div>
      </div>

      {moreFiltersShown && (
        <div className="card mt-2">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 col-lg">
                <div className="mb-3">
                  <label htmlFor="min_start_date" className="form-label">
                    Move-in date is between:
                  </label>
                  <div className="row g-2">
                    <div className="col">
                      <input
                        type="date"
                        className="form-control"
                        id="min_start_date"
                        name="min_start_date"
                        onChange={handleFormChange}
                        value={formData.min_start_date}
                      />
                    </div>
                    <span className="col-auto mx-0 text-center" style={{ paddingTop: '0.5rem' }}>
                      and
                    </span>
                    <div className="col">
                      <input
                        type="date"
                        className="form-control"
                        id="max_start_date"
                        name="max_start_date"
                        onChange={handleFormChange}
                        value={formData.max_start_date}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="pictures"
                    checked={formData.pictures}
                    value={formData.pictures}
                    onChange={handleFormChange}
                    name="pictures"
                  />
                  <label className="form-check-label" htmlFor="pictures">
                    Only show listings with pictures
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="wheelchair_access"
                    checked={formData.wheelchair_access}
                    value={formData.wheelchair_access}
                    onChange={handleFormChange}
                    name="wheelchair_access"
                  />
                  <label className="form-check-label" htmlFor="wheelchair_access">
                    Handicap/disability access
                  </label>
                </div>
              </div>
              <div className="col-md-6 col-lg">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="pets"
                    checked={formData.pets}
                    value={formData.pets}
                    onChange={handleFormChange}
                    name="pets"
                  />
                  <label className="form-check-label" htmlFor="pets">
                    Pets allowed
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="laundry"
                    checked={formData.laundry}
                    value={formData.laundry}
                    onChange={handleFormChange}
                    name="laundry"
                  />
                  <label className="form-check-label" htmlFor="laundry">
                    Washer/dryer included
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="utilities"
                    checked={formData.utilities}
                    value={formData.utilities}
                    onChange={handleFormChange}
                    name="utilities"
                  />
                  <label className="form-check-label" htmlFor="utilities">
                    Some utilities included
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="furnished"
                    checked={formData.furnished}
                    value={formData.furnished}
                    onChange={handleFormChange}
                    name="furnished"
                  />
                  <label className="form-check-label" htmlFor="furnished">
                    Furnished unit
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="parking"
                    checked={formData.parking}
                    value={formData.parking}
                    onChange={handleFormChange}
                    name="parking"
                  />
                  <label className="form-check-label" htmlFor="parking">
                    Designated parking included
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="air_conditioning"
                    checked={formData.air_conditioning}
                    value={formData.air_conditioning}
                    onChange={handleFormChange}
                    name="air_conditioning"
                  />
                  <label className="form-check-label" htmlFor="air_conditioning">
                    Air conditioning
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
